import { type FC, memo } from "react";
import { type PriceData } from "~/domains/pricing/types/PriceData";
import { MultiPrice, Price, PriceType, RebateLabelType } from "~/domains/pricing/components/Price/v1/Price";
import { getRebatePercentage } from "~/domains/product/utils/productDescriptorUtils";
import { useSelector } from "react-redux";
import { selectProperAmountInCart } from "~/features/addToCart/redux/cartAmounts/cartAmountSelectors";
import { canSeeListPrice } from "~/utils/features";
import type { NetPriceProps } from "~/features/productDetails/components/ProductSummary/components/NetPrice";
import type { ProductDescriptor } from "~/domains/product/types/ProductDescriptor";

interface PdpPriceProps {
  price?: PriceData | null;
  priceUnit: number;
  isMultiPrice?: boolean;
  isMonthlySubsription?: boolean;
  isSpecialPrice?: boolean;
  includesSurcharge?: boolean;
  tariffSurcharge?: boolean;
  quantity: number;
  product: ProductDescriptor;

  rebateVariant?: RebateLabelType;
}

const PurchaserPdpPrice: FC<PdpPriceProps> = ({
  price,
  isMultiPrice,
  priceUnit,
  isMonthlySubsription,
  isSpecialPrice,
  includesSurcharge,
  tariffSurcharge,
  product,
  quantity,
  rebateVariant = RebateLabelType.Full,
}) => {
  const props: NetPriceProps = {
    product: product,
    packCode: "",
    quantity: quantity,
  };

  const totalQuantity =
    props.quantity +
    useSelector(
      selectProperAmountInCart({
        packCode: "",
        priceReference: "",
        productCode: product.base.code,
      }),
    );
  if (isMultiPrice) {
    return <MultiPrice as="h2" classes={{ root: "scha__pdp__price-header", price: "f-bold" }} />;
  }

  const rootCls = price && price.value > 0 ? "scha__pdp__price-header" : "scha__pdp__price-header--price-request";
  const priceCls = price && price.value > 0 ? "f-bold" : undefined;

  const listPriceVisible = canSeeListPrice({
    product: props.product,
    isSpecialPrice: isSpecialPrice,
    hasSurcharge: includesSurcharge,
  });
  const rebate = listPriceVisible
    ? getRebatePercentage({
        product: props.product,
        packCode: props.packCode,
        totalQuantity: totalQuantity,
      })
    : null;

  return (
    <Price
      as="h2"
      classes={{
        root: rootCls,
        price: priceCls,
        label: "label-large",
        additionalLabel: "scha__customer-special-price",
      }}
      variant={PriceType.ProductNetPriceFullLabel}
      price={price}
      unit={priceUnit}
      rebateVariant={rebateVariant}
      rebateValue={rebate}
      isSubscription={isMonthlySubsription}
      isSpecialPrice={isSpecialPrice}
      includesSurcharge={includesSurcharge}
      tariffSurcharge={tariffSurcharge}
    />
  );
};

export default memo(PurchaserPdpPrice);
